/**
 * Pagination — control compacto con números y elipsis (v1.5.0-rc2).
 *
 * Diseño:
 *   < 1 2 3 ... 47 >    (siempre muestra primera, última, actual ±1-2)
 *
 * Comportamiento:
 *   - Si totalPages ≤ 7 → muestra todas las páginas sin elipsis.
 *   - Si totalPages > 7 → primera + (actual ±1) + última, con "..." en gaps.
 *   - Los botones < y > están deshabilitados cuando estás en los extremos.
 *
 * Props:
 *   - page: página actual (1-indexed)
 *   - totalPages: número total de páginas
 *   - onPageChange: callback (newPage: number) => void
 *   - total?: número total de elementos (opcional, se muestra como "X resultados")
 *   - size?: tamaño actual por página (opcional, se muestra como "N por página")
 *   - isFetching?: indicador de "actualizando" mientras carga
 *
 * Ejemplo:
 *   <Pagination
 *     page={page}
 *     totalPages={data?.pages ?? 1}
 *     total={data?.total}
 *     size={data?.size}
 *     onPageChange={setPage}
 *     isFetching={isFetching}
 *   />
 */
import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface PaginationProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  total?: number;
  size?: number;
  isFetching?: boolean;
}

/**
 * Construye la lista de "items" a renderizar: números de página o "ellipsis".
 *
 * Algoritmo:
 *   - totalPages ≤ 7: muestra [1..totalPages] sin elipsis.
 *   - totalPages > 7: muestra [1, ...?, page-1, page, page+1, ...?, totalPages]
 *     con "..." donde haya gap > 1 entre números consecutivos.
 *   - Si page está cerca del inicio/final, expande el rango para no perder
 *     contexto visual (siempre muestra al menos 3 consecutivos cuando posible).
 */
function buildPageItems(page: number, totalPages: number): (number | "ellipsis")[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const items: (number | "ellipsis")[] = [];
  const window = new Set<number>();

  // Siempre incluir primera y última
  window.add(1);
  window.add(totalPages);

  // Ventana alrededor de la actual (±1)
  for (let p = page - 1; p <= page + 1; p++) {
    if (p >= 1 && p <= totalPages) window.add(p);
  }

  // Si actual está cerca del inicio, mostrar 2-3-4-5 también
  if (page <= 3) {
    [2, 3, 4, 5].forEach((p) => p <= totalPages && window.add(p));
  }
  // Si actual está cerca del final, mostrar las últimas también
  if (page >= totalPages - 2) {
    [totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1].forEach(
      (p) => p >= 1 && window.add(p),
    );
  }

  const sorted = [...window].sort((a, b) => a - b);
  for (let i = 0; i < sorted.length; i++) {
    items.push(sorted[i]);
    // Insertar elipsis si hay gap > 1 al siguiente
    if (i + 1 < sorted.length && sorted[i + 1] - sorted[i] > 1) {
      items.push("ellipsis");
    }
  }

  return items;
}

export function Pagination({
  page,
  totalPages,
  onPageChange,
  total,
  size,
  isFetching = false,
}: PaginationProps) {
  if (totalPages <= 0) return null;

  // Si solo hay 1 página, no mostrar controles pero sí el "X resultados"
  // (si se proveen total/size) — útil para que el usuario vea "sólo hay 3 ítems".
  if (totalPages === 1) {
    if (total === undefined && size === undefined) return null;
    return (
      <div className="flex items-center justify-between border-t px-3 py-2 text-xs">
        <span className="text-muted-foreground">
          {total !== undefined ? `${total} resultado${total === 1 ? "" : "s"}` : null}
          {total !== undefined && size !== undefined ? " · " : null}
          {size !== undefined ? `${size} por página` : null}
          {isFetching && " · actualizando…"}
        </span>
      </div>
    );
  }

  const items = buildPageItems(page, totalPages);
  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <div className="flex items-center justify-between gap-2 border-t px-3 py-2 text-xs">
      <span className="text-muted-foreground">
        {total !== undefined ? (
          <>
            <span className="font-medium text-foreground">{total.toLocaleString()}</span>
            <span className="max-md:hidden"> resultado{total === 1 ? "" : "s"} · página {page} de {totalPages}</span>
            <span className="md:hidden"> · pág {page}/{totalPages}</span>
          </>
        ) : (
          <>
            Página {page} de {totalPages}
          </>
        )}
        {isFetching && " · actualizando…"}
      </span>

      <div className="flex items-center gap-1">
        <Button
          size="sm"
          variant="outline"
          disabled={!canPrev}
          onClick={() => onPageChange(Math.max(1, page - 1))}
          aria-label="Página anterior"
          className="h-7 w-7 p-0 max-md:w-9"
        >
          <ChevronLeft size={14} />
        </Button>

        {items.map((it, i) =>
          it === "ellipsis" ? (
            <span
              key={`e-${i}`}
              aria-hidden="true"
              className="px-1 text-muted-foreground select-none max-md:hidden"
            >
              …
            </span>
          ) : (
            <Button
              key={it}
              size="sm"
              variant={it === page ? "default" : "outline"}
              onClick={() => onPageChange(it)}
              aria-label={`Ir a página ${it}`}
              aria-current={it === page ? "page" : undefined}
              // rc138 — móvil: solo la página actual (los números no caben)
              className={cn("h-7 min-w-[28px] px-2 tabular-nums", it !== page && "max-md:hidden")}
            >
              {it}
            </Button>
          ),
        )}

        <Button
          size="sm"
          variant="outline"
          disabled={!canNext}
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          aria-label="Página siguiente"
          className="h-7 w-7 p-0 max-md:w-9"
        >
          <ChevronRight size={14} />
        </Button>
      </div>
    </div>
  );
}
